import { Text as HeadingText } from "@certn/cascadia";
import { SectionMessage } from "@certn/cascadia-react";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { useContext } from "react";

import { BaseLayout } from "./BaseLayout";
import { BaseLayoutContent } from "./BaseLayoutContent";
import { CascadiaJsonFormContext } from "../CascadiaJsonFormContext";
import { Text } from "../components/Text";
import { RenderError } from "../components/helpers/RenderError";
import { isCategoryLayout } from "../utils";
import {
  CascadiaLayoutProps,
  withCascadiaLayoutProps,
} from "../wrappers/withCascadiaLayoutProps";

export function CategoryLayout(props: CascadiaLayoutProps) {
  const { isSummaryForPath, formID } = useContext(CascadiaJsonFormContext);

  const { uischema, path } = props;
  const options = uischema.options || {};

  if (!isCategoryLayout(uischema)) {
    return <RenderError>Invalid Category</RenderError>;
  }

  if (isSummaryForPath?.(path)) {
    return (
      <BaseLayout
        testID="category-layout"
        {...props}
        className={styles.summary}
      />
    );
  }

  return (
    <BaseLayout testID="category-layout" {...props} className={styles.layout}>
      <div className={styles.heading}>
        {uischema.label && (
          <HeadingText
            as="h1"
            style="form/page-title"
            testID={formID ? `${formID}-title` : `${slugify(props.path)}-title`}
          >
            {uischema.label}
          </HeadingText>
        )}
        {options.subLabel && (
          <Text
            style="form/body-primary"
            color="text-weak"
            data-testid={
              formID
                ? `${formID}-sub-title`
                : `${slugify(props.path)}-sub-title`
            }
          >
            {options.subLabel}
          </Text>
        )}
        {options.sectionMessage && (
          <SectionMessage
            type={options.sectionMessage.type}
            sectionTitle={options.sectionMessage.heading}
            icon={options.sectionMessage.type}
            testID="category-section-message"
          >
            {options.sectionMessage.content}
          </SectionMessage>
        )}
      </div>

      <div
        className={css(styles.layout, {
          '> :not([aria-hidden:"true"]):not(:last-child)': styles.divider,
        })}
      >
        <BaseLayoutContent />
      </div>
    </BaseLayout>
  );
}

const styles = {
  summary: css({
    padding: "var(--space-4)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  layout: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  heading: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  divider: css({
    paddingBottom: "var(--space-4)",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "var(--border-base)",
  }),
};

CategoryLayout.displayName = "CategoryLayout";

export const CategoryLayoutRenderer = withCascadiaLayoutProps(CategoryLayout);
